/**
 * Local filter over cached rows (spec §14 `fu`): applied instantly on every keystroke while the
 * server search is debounced, and the only search available offline.
 */
import { labelsOf } from '$lib/mail/labels';
import type { MessageRow } from '$lib/types';
import type { ParsedQuery } from './grammar';

const has = (hay: string | null | undefined, needle: string) => (hay || '').toLowerCase().includes(needle.toLowerCase());

export function matchesLocally(row: MessageRow, q: ParsedQuery): boolean {
  if (q.from && !(has(row.fromName, q.from) || has(row.fromAddr, q.from))) return false;
  if (q.to && !(row.toAddrs || []).some((a) => has(a, q.to!))) return false;
  if (q.subject && !has(row.subject, q.subject)) return false;
  if (q.unseen && row.flags.includes('\\Seen')) return false;
  if (q.seen && !row.flags.includes('\\Seen')) return false;
  if (q.flagged && !row.flags.includes('\\Flagged')) return false;
  if (q.hasAttachment && !row.hasAttachments) return false;
  if (q.list && !row.list) return false;
  if (q.personal && row.list) return false;
  if (q.label && !labelsOf(row.flags).includes(q.label)) return false;
  if (q.after || q.before) {
    const t = Date.parse(row.receivedAt || row.date || '');
    if (Number.isNaN(t)) return false;
    if (q.after && t < Date.parse(q.after + 'T00:00:00Z')) return false;
    if (q.before && t >= Date.parse(q.before + 'T00:00:00Z')) return false;
  }
  if (q.text) {
    const n = q.text;
    if (!(has(row.subject, n) || has(row.fromName, n) || has(row.fromAddr, n) || has(row.snippet || '', n))) return false;
  }
  return true;
}

export function filterLocal<T extends MessageRow>(rows: readonly T[], q: ParsedQuery): T[] {
  return rows.filter((r) => matchesLocally(r, q));
}
